import { useEffect, useMemo, useState } from "react";
import { CalendarPlus, X, Loader2, CheckCircle2 } from "lucide-react";
import { api } from "@/lib/api";
import { AREA_LABELS } from "@/components/BodyMap";

// Simple modal (Shadcn Dialog isn't wired for this page, and we want zero
// portal shenanigans over the body-map). Roll a tiny in-place overlay.
export default function QuickStretchPlanDialog({ area, baseArea, open, onClose, onCreated }) {
  const prettyLabel = AREA_LABELS?.[area] || (baseArea || area || "").replace(/_/g, " ");
  const [day1, setDay1] = useState(1);
  const [day2, setDay2] = useState(15);
  const [time, setTime] = useState("12:00");
  const [duration, setDuration] = useState(15);
  const [months, setMonths] = useState(6);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [done, setDone] = useState(null); // number of sessions created

  useEffect(() => {
    if (open) {
      setDay1(1); setDay2(15); setTime("12:00"); setDuration(15); setMonths(6);
      setBusy(false); setErr(""); setDone(null);
    }
  }, [open, area]);

  const bodyPartsPayload = useMemo(() => {
    // Prefer the exact clicked region (e.g. shoulder_left) when we have it,
    // otherwise fall back to the base (e.g. shoulder).
    return area ? [area] : baseArea ? [baseArea] : [];
  }, [area, baseArea]);

  // Compute the concrete list of dates we'll create. Purely for the preview.
  const previewDates = useMemo(() => {
    const now = new Date();
    // Start next month if we're already past both days this month, otherwise
    // start this month at the first upcoming day.
    const out = [];
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    let year = now.getFullYear();
    let month = now.getMonth();
    let started = false;
    while (out.length < months * 2) {
      for (const dom of [day1, day2].sort((a, b) => a - b)) {
        const d = new Date(year, month, dom);
        if (!started && d < today) continue;
        started = true;
        out.push(d);
        if (out.length >= months * 2) break;
      }
      month += 1;
      if (month > 11) { month = 0; year += 1; }
    }
    return out;
  }, [day1, day2, months]);

  if (!open) return null;

  const submit = async () => {
    setErr("");
    if (day1 < 1 || day1 > 28 || day2 < 1 || day2 > 28) {
      setErr("Days must be between 1 and 28.");
      return;
    }
    if (day1 === day2) {
      setErr("Pick two different days of the month.");
      return;
    }
    if (!time) return setErr("Pick a time.");
    if (!duration || duration <= 0) return setErr("Duration must be at least 1 minute.");
    if (!bodyPartsPayload.length) return setErr("Missing body part.");
    setBusy(true);
    try {
      // We issue TWO monthly series (one for day1, one for day2) each with
      // `max_occurrences = months`. Total sessions = months * 2.
      const title = `${prettyLabel} focus stretch`;
      const orderedDays = [day1, day2].sort((a, b) => a - b);
      let created = 0;
      // Start from the first upcoming occurrence for each day. We compute
      // the first date >= today for that day-of-month.
      const now = new Date();
      const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
      for (const dom of orderedDays) {
        let year = now.getFullYear();
        let month = now.getMonth();
        let first = new Date(year, month, dom);
        if (first < today) {
          month += 1;
          if (month > 11) { month = 0; year += 1; }
          first = new Date(year, month, dom);
        }
        const [hh, mm] = time.split(":").map(Number);
        first.setHours(hh, mm, 0, 0);
        const iso = first.toISOString();
        const r = await api.post("/clinic/scheduled-stretches", {
          title,
          scheduled_at: iso,
          duration_minutes: Number(duration),
          body_parts: bodyPartsPayload,
          recurrence: "monthly",
          max_occurrences: Number(months),
          notes: `Quick plan · ${prettyLabel} · 2x/month for ${months} months`,
        });
        created += (r.data?.occurrences || 0);
      }
      setDone(created);
      onCreated?.(created);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      setErr(typeof detail === "string" ? detail : (e.message || "Could not create schedule."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-zinc-900/50 backdrop-blur-sm"
      role="dialog"
      aria-modal="true"
      data-testid="quick-stretch-plan-dialog"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      <div className="bg-white w-full max-w-lg rounded-2xl shadow-2xl border border-zinc-200 overflow-hidden">
        <div className="px-5 py-4 border-b border-zinc-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <CalendarPlus className="w-5 h-5" style={{ color: "var(--gold-hover, #b5952f)" }} />
            <h3 className="font-bold text-lg">Quick insert stretch plan</h3>
          </div>
          <button onClick={onClose} className="text-zinc-400 hover:text-zinc-700" data-testid="quick-plan-close" aria-label="Close">
            <X className="w-5 h-5" />
          </button>
        </div>

        <div className="p-5 space-y-4 text-sm">
          <div className="rounded-lg bg-zinc-50 border border-zinc-200 px-3 py-2 text-xs text-zinc-600">
            Target: <span className="font-bold text-zinc-900 capitalize">{prettyLabel}</span> · 2× per month · {duration} min · {months} months
          </div>

          {done != null ? (
            <div
              className="rounded-xl border border-emerald-300 bg-emerald-50 text-emerald-800 px-4 py-6 text-center"
              data-testid="quick-plan-success"
            >
              <CheckCircle2 className="w-8 h-8 mx-auto mb-2" />
              <div className="font-bold text-lg">Plan scheduled</div>
              <div className="text-sm mt-1">Created {done} sessions on the clinic calendar.</div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-2 gap-3">
                <label className="block">
                  <span className="text-xs font-semibold text-zinc-600">First day of month</span>
                  <input
                    type="number" min={1} max={28} value={day1}
                    onChange={(e) => setDay1(Number(e.target.value))}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-zinc-200 focus:border-zinc-900 focus:outline-none"
                    data-testid="quick-plan-day1"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-zinc-600">Second day of month</span>
                  <input
                    type="number" min={1} max={28} value={day2}
                    onChange={(e) => setDay2(Number(e.target.value))}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-zinc-200 focus:border-zinc-900 focus:outline-none"
                    data-testid="quick-plan-day2"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-zinc-600">Time</span>
                  <input
                    type="time" value={time}
                    onChange={(e) => setTime(e.target.value)}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-zinc-200 focus:border-zinc-900 focus:outline-none"
                    data-testid="quick-plan-time"
                  />
                </label>
                <label className="block">
                  <span className="text-xs font-semibold text-zinc-600">Duration (min)</span>
                  <input
                    type="number" min={1} max={240} value={duration}
                    onChange={(e) => setDuration(Number(e.target.value))}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-zinc-200 focus:border-zinc-900 focus:outline-none"
                    data-testid="quick-plan-duration"
                  />
                </label>
                <label className="block col-span-2">
                  <span className="text-xs font-semibold text-zinc-600">Months to schedule ahead</span>
                  <input
                    type="number" min={1} max={12} value={months}
                    onChange={(e) => setMonths(Number(e.target.value))}
                    className="mt-1 w-full h-10 px-3 rounded-md border border-zinc-200 focus:border-zinc-900 focus:outline-none"
                    data-testid="quick-plan-months"
                  />
                </label>
              </div>

              <div className="text-xs text-zinc-500">
                Preview: {previewDates.length} sessions · first on{" "}
                <span className="font-semibold text-zinc-800">
                  {previewDates[0]?.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </span>{" "}
                · last on{" "}
                <span className="font-semibold text-zinc-800">
                  {previewDates[previewDates.length - 1]?.toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
                </span>
              </div>

              {err && (
                <div className="rounded-md border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-xs" data-testid="quick-plan-error">
                  {err}
                </div>
              )}
            </>
          )}
        </div>

        <div className="px-5 py-4 border-t border-zinc-200 flex items-center justify-end gap-2 bg-zinc-50">
          {done != null ? (
            <button
              onClick={onClose}
              className="h-10 px-5 rounded-md bg-zinc-900 text-white font-semibold text-sm hover:bg-zinc-800"
              data-testid="quick-plan-done"
            >
              Done
            </button>
          ) : (
            <>
              <button
                onClick={onClose}
                className="h-10 px-4 rounded-md border border-zinc-200 text-zinc-700 font-semibold text-sm hover:bg-white"
                data-testid="quick-plan-cancel"
              >
                Cancel
              </button>
              <button
                onClick={submit}
                disabled={busy}
                className="h-10 px-5 rounded-md bg-zinc-900 text-white font-semibold text-sm hover:bg-zinc-800 disabled:opacity-60 inline-flex items-center gap-2"
                data-testid="quick-plan-submit"
              >
                {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarPlus className="w-4 h-4" />}
                Schedule {months * 2} sessions
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
